import React from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Link,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import Navbar from "../components/Navbar";
import darkLogo from "../assets/images/Newlogo-black.png";
import BackToTopButton from "../components/common/BackToTopButton";
import Footer from "./Footer";
import JoinCommunitySection from "./common/JoinCommunitySection";
import { Helmet } from "react-helmet-async";

const PrivacyPolicy = () => {
  return (
    <><Helmet>
    <title>Privacy Policy&nbsp;| NOMAD360</title>
  </Helmet>
  
    <Box position="absolute" top={0} left={0} width="100%" zIndex="2">
      {/* Navbar */}
      <Navbar
        bgColor="white"
        linkColor="black"
        styling="outline"
        logoSrc={darkLogo}
        languageCodeColor="black"
        userProfileBgColor="transparent"
      />

      <Box
        maxW={{ base: "100vw", sm: "85vw", md: "85vw", lg: "60vw" }}
        mx="auto"
        px={4}
        py={8}
        pt={8}
      >
        <Heading as="h1" size="xl" align="center" mt={10} mb={10}>
          Privacy Policy NOMAD360
        </Heading>
        <VStack align="start" spacing={4}>
          {/* Title */}

          <Text mb={0}>
            This is the privacy policy (hereinafter referred to as the
            "Statement") for NOMAD360, a service provided by Lancewood AI
            Solutions B.V. In this Statement, we clearly and concisely explain
            who we are, what personal data we process, why we collect this data,
            and how we use it. By personal data, we mean any information or
            collection of data that enables us to identify you directly or
            indirectly, such as your first and last name, email address, date of
            birth, and more.
          </Text>

          <Text mb={0}>
            We may amend this Statement from time to time. The most up-to-date
            version of this Statement can always be found on our website:{" "}
            <Link
              color="blue.500"
              href="https://nomad360.io/policies/privacy-policy"
            >
              nomad360.io/policies/privacy-policy
            </Link>
            . We encourage you to consult this Statement regularly in order to
            be informed of any changes.
          </Text>

          <Text>This Statement was last updated on: April 14, 2025.</Text>

          {/* Who are we? */}
          <Heading as="h2" size="lg" mt={0}>
            Who are we?
          </Heading>
          <Text>
            NOMAD360 is a platform that allows users, using an AI model, to
            generate personalized travel recommendations and other content based
            on information they provide themselves, such as travel preferences
            and destination details. NOMAD360 is provided by Lancewood AI
            Solutions B.V. In this Statement, "we", "us" or "NOMAD360" refer to
            the controllers of your personal data. References to NOMAD360 refer
            specifically to Lancewood AI Solutions B.V. (Chamber of Commerce
            number: 95481214 ).
          </Text>

          {/* When does this Statement apply? */}
          <Heading as="h2" size="lg">
            When does this Statement apply?
          </Heading>
          <Text mb={0}>
            This Statement describes how we collect and use personal data, for
            example when you:
          </Text>
          <VStack align="start" pl={6}>
            <Text mb={0}>• Visiting the NOMAD360 website;</Text>
            <Text mb={0}>• Using our social media channels;</Text>
            <Text mb={0}>• Having an account on NOMAD360;</Text>
            <Text mb={0}>• Using the services or features of NOMAD360;</Text>
            <Text mb={0}>• Contacting us for any reason.</Text>
          </VStack>

          {/* What personal data do we process, and why? */}
          <Heading as="h2" size="lg">
            What personal data do we process, and why?
          </Heading>

          {/* 1. Personal data of persons with an account */}
          <Heading as="h3" size="md">
            1. Personal data of persons with an account for NOMAD360
          </Heading>
          <Text>
            When you create an account on NOMAD360, we collect the following
            personal data:
          </Text>

          {/* Table: categories & examples */}
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Categories of</Th>
                <Th>Example</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>a) Contact details</Td>
                <Td>E-mail address, first and last name</Td>
              </Tr>
              <Tr>
                <Td>b) Login details</Td>
                <Td>Email address, password, or Google account</Td>
              </Tr>
              <Tr>
                <Td>c) Age</Td>
                <Td>Whether or not 16 years or older</Td>
              </Tr>
              <Tr>
                <Td>d) Settings and preferences</Td>
                <Td>
                  Default country, language, currency, (travel) preferences
                </Td>
              </Tr>
              <Tr>
                <Td>e) User location</Td>
                <Td>
                  IP address, location data derived from GPS or other
                  technologies
                </Td>
              </Tr>
              <Tr>
                <Td>f) Activity within the social community</Td>
                <Td>
                  User profiles, messages and interactions within the community
                </Td>
              </Tr>
              <Tr>
                <Td>g) Financial data</Td>
                <Td>
                  Bank and payment details, financial information entered for
                  the &quot;budget planner&quot; within NOMAD360
                </Td>
              </Tr>
            </Tbody>
          </Table>

          <Text>
            Purposes and legal basis on which we process the above categories of
            personal data:
          </Text>

          {/* Table: purposes & legal basis */}
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Target</Th>
                <Th>Legal</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>
                  a) Maintaining contact and communicating about questions or
                  other information via e-mail
                </Td>
                <Td>
                  Necessary for the performance of the agreement between you and
                  Lancewood AI Solutions; Article (Art. 6(1)(b) AVG)
                </Td>
              </Tr>
              <Tr>
                <Td>
                  Approaching you for commercial offers, newsletters and
                  promotions that may be of interest to you
                </Td>
                <Td>
                  Legitimate interest of Lancewood AI Solutions to send
                  marketing to existing users; Article 6(1)(f) AVG
                </Td>
              </Tr>
              <Tr>
                <Td>
                  Providing feedback on, and contacting you in response to,
                  completed feedback forms
                </Td>
                <Td>Your consent (Art. 6(1)(a) AVG)</Td>
              </Tr>
              <Tr>
                <Td>
                  b) Creating a username and account and providing access to
                  NOMAD360
                </Td>
                <Td>
                  Necessary for the performance of the agreement between you and
                  Lancewood AI Solutions; Article 6(1)(b) AVG
                </Td>
              </Tr>
              <Tr>
                <Td>
                  c) Assessing whether you have reached the age of 16 years{" "}
                </Td>
                <Td>
                  Necessary to comply with legal obligations under Article 8
                  AVG; Article 6(1)(c) AVG
                </Td>
              </Tr>
              <Tr>
                <Td>
                  d) Tailoring our services to your preferences and user
                  experience
                </Td>
                <Td>Your consent (Art. 6(1)(a) AVG)</Td>
              </Tr>
              <Tr>
                <Td>
                  e) Using location data to activate specific functionalities
                  within NOMAD360, such as offering content or services relevant
                  to your location
                </Td>
                <Td>Your consent (Art. 6(1)(a) AVG)</Td>
              </Tr>
              <Tr>
                <Td>f) Managing the social community within NOMAD360 </Td>
                <Td>
                  Necessary for the performance of the agreement between you and
                  Lancewood AI Solutions; Article 6(1)(b) AVG
                </Td>
              </Tr>
              <Tr>
                <Td>g) Processing payments or refunds </Td>
                <Td>
                  Necessary for the performance of the agreement between you and
                  Lancewood AI Solutions; Article 6(1)(b) AVG
                </Td>
              </Tr>
              <Tr>
                <Td>
                  Storing financial data to enable users to monitor their
                  spending and create budgets in the "budget planner"
                </Td>
                <Td>
                  Necessary for the performance of the agreement between you and
                  Lancewood AI Solutions; Article 6(1)(b) AVG
                </Td>
              </Tr>
            </Tbody>
          </Table>

          <Text>
            If you no longer wish to receive commercial offers, you can easily
            unsubscribe via the 'unsubscribe' button at the bottom of the email
            or by sending an email to info@nomad360.io.
          </Text>

          {/* 2. Personal data of persons using the services */}
          <Heading as="h3" size="md">
            2. Personal data of persons using the services and/or
            functionalities of NOMAD360
          </Heading>
          <Text>
            When you use NOMAD360's services, such as generating personalized
            travel advice or other content, we may collect the following
            personal data:
          </Text>

          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Categories of</Th>
                <Th>Example</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>a) User content</Td>
                <Td>
                  Uploaded information from yourself that you provide for
                  content personalization, such as name, address details and
                  travel preferences
                </Td>
              </Tr>
              <Tr>
                <Td>b) User data</Td>
                <Td>
                  Click behavior, usage patterns within the application,
                  interactions within the social community, Areas of interest
                  and preferences derived from your use of the services, IP
                  address, device data, location information and other technical
                  metadata
                </Td>
              </Tr>
              <Tr>
                <Td>c) Financial data (for CO2 offsetting)</Td>
                <Td>
                  Payment details related to donations made through NOMAD360
                </Td>
              </Tr>
              <Tr>
                <Td>d) Wallet info for NFT delivery</Td>
                <Td>
                  Crypto wallet address or, in its absence, an e mail address
                  for creating a Crossmint wallet
                </Td>
              </Tr>
            </Tbody>
          </Table>

          <Text>
            Purposes and legal basis on which we process the above categories of
            personal data:
          </Text>

          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Target</Th>
                <Th>Legal</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>a) Delivering personalized content and travel advice</Td>
                <Td>
                  Necessary for the performance of the agreement between you and
                  Lancewood AI Solutions; Article 6(1)(b) AVG
                </Td>
              </Tr>
              <Tr>
                <Td>
                  b) Training our algorithms, including LLMs and social
                  community functionalities, to improve the quality and
                  relevance of our services
                </Td>
                <Td>User consent (Art. 6(1)(a) AVG)</Td>
              </Tr>
              <Tr>
                <Td>Personalizing ads and promotional content</Td>
                <Td>User consent (Art. 6(1)(a) AVG)</Td>
              </Tr>
              <Tr>
                <Td>
                  Monitoring and analyzing the use of our services in order to
                  improve them
                </Td>
                <Td>User consent (Art. 6(1)(a) AVG)</Td>
              </Tr>
              <Tr>
                <Td>c) Processing payments or refunds</Td>
                <Td>
                  Necessary for the performance of the agreement between you and
                  Lancewood AI Solutions; Article 6(1)(b) AVG
                </Td>
              </Tr>
              <Tr>
                <Td>d) Sending an NFT as a memento of the contribution </Td>
                <Td>
                  Necessary for the performance of the agreement between you and
                  Lancewood AI Solutions; Article 6(1)(b) AVG
                </Td>
              </Tr>
            </Tbody>
          </Table>

          {/* 3. Personal data of persons who contact us */}
          <Heading as="h3" size="md">
            3. Personal data of persons who contact us
          </Heading>
          <Text>
            If you do not belong to one of the above categories, but contact us
            (e.g., via the contact form, email, phone, or social media), we may
            collect:
          </Text>

          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Categories of</Th>
                <Th>Example</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>a) Personal identification data</Td>
                <Td>First name, surname</Td>
              </Tr>
              <Tr>
                <Td>b) Contact details</Td>
                <Td>Email, phone number</Td>
              </Tr>
              <Tr>
                <Td>c) Communications data</Td>
                <Td>
                  Content of e-mail messages, instant messages, or other data
                  you share with us
                </Td>
              </Tr>
            </Tbody>
          </Table>

          <Text>
            Purposes and legal basis on which we process the above categories of
            personal data:
          </Text>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Target</Th>
                <Th>Legal</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>a) Maintaining contact and</Td>
                <Td>
                  Lancewood AI Solutions' legitimate interest in communicating
                  with contacting individuals; Article 6(1)(f) AVG
                </Td>
              </Tr>
              <Tr>
                <Td>b) Communicating about questions or other </Td>
                <Td>
                  Lancewood AI Solutions' legitimate interest in communicating
                  with contacting individuals; Article 6(1)(f) AVG
                </Td>
              </Tr>
              <Tr>
                <Td>c) Information</Td>
                <Td>
                  Lancewood AI Solutions' legitimate interest in communicating
                  with contacting individuals; Article 6(1)(f) AVG
                </Td>
              </Tr>
            </Tbody>
          </Table>

          {/* Who do we share personal data with? */}
          <Heading as="h2" size="lg" mt={6}>
            Who do we share personal data with?
          </Heading>
          <Text>
            We do not sell personal data to third parties. We also not share
            your personal data without your consent, except when required by
            law, or in a limited number of cases described below. To fulfil the
            purposes set out in this Statement, we may share your personal data
            with the following entities:
          </Text>
          <VStack align="start" pl={6}>
            <Text mb={0}>
              •{" "}
              <strong>
                Affiliated companies within Lancewood AI Solutions:
              </strong>{" "}
              due to our organizational structure, your personal data may be
              shared with certain affiliates of Lancewood AI Solutions,
              including Lancewood Holding B.V. Access to this personal data is
              limited to employees who require it for their duties.
            </Text>
            <Text mb={0}>
              • <strong>External service providers:</strong> some processing
              activities are outsourced to trusted third parties who perform
              certain tasks on our behalf, such as hosting our website, IT
              services, consultancy, delivery services, payment processors,
              electronic communication platform providers and marketing
              services.
            </Text>
            <Text mb={0}>
              • <strong>Professional advisers and others:</strong> your personal
              data may also be shared with professional advisers, such as banks,
              insurance companies, accountants, lawyers and other experts.
            </Text>
            <Text mb={0}>
              • <strong>Other parties in business transactions:</strong> your
              personal data may occasionally be shared in the context of
              business transactions, such as the sale of parts of our business,
              or in reorganizations, mergers, joint ventures or other forms of
              acquisition of our business, assets or shares, including in the
              event of bankruptcy or similar proceedings.
            </Text>
          </VStack>

          <Text>
            If personal data is shared with a third party, we will, where
            necessary, enter into a (processor) agreement with this third party
            that meets all the requirements of the General Data Protection
            Regulation (AVG).
          </Text>

          {/* How long do we keep personal data? */}
          <Heading as="h2" size="lg">
            How long do we keep personal data?
          </Heading>
          <Text>
            We keep your personal data for as long as necessary for the purposes
            for which we process it, unless a longer retention period is
            required by law. See the section "What personal data do we process,
            and why?" for more information on processing purposes. Our retention
            periods are determined based on the following criteria:
          </Text>
          <VStack align="start" pl={6}>
            <Text mb={0}>
              • <strong>Necessity for service or business operations:</strong>{" "}
              how long is the data needed to provide you with our services or
              support our business operations?
            </Text>
            <Text mb={0}>
              • <strong>Conversations within NOMAD360:</strong> conversations
              you have within NOMAD360 are stored for as long as your account is
              active or until you decide to delete your account. This data is
              stored securely on the servers of our engaged hosting provider and
              is not shared with third parties.
            </Text>
            <Text mb={0}>
              • <strong>Account on NOMAD360:</strong> as long as your created
              account is active, we will keep your account details. Deleting
              your account can be done easily via NOMAD360. If the account has
              been inactive for 12 months, you will receive an email asking
              whether you want to keep or delete the account. If you do not log
              in within 2 weeks of our e-mail or do not otherwise inform us that
              you wish to keep the account, we will be deleting your account
              automatically, except for data that we are required to keep by
              law.
            </Text>
            <Text mb={0}>
              • <strong>Legal and contractual obligations:</strong> sometimes we
              are required to keep data for longer periods. This may be required
              by law, a government request related to an investigation, or to
              support legal proceedings or protect us against potential claims,
              for example.
            </Text>
          </VStack>

          {/* Cookies */}
          <Heading as="h2" size="lg">
            Cookies
          </Heading>
          <Text mb={0}>
            When you visit or use our website, it automatically collects
            information sent from your computer, mobile phone, or other devices.
            We use various types of cookies and similar technologies on our
            website, including functional cookies, statistics cookies, and
            marketing/tracking cookies.
          </Text>
          <Text mb={0}>
            In some cases, cookies may contain personal data, such as a unique
            ID linked to your device, IP address, or, when logged in,
            information traceable to your account. For instance, marketing and
            tracking cookies may process personal data to display targeted ads
            and analyze user behavior. For these cookies, we ask for your
            explicit consent.{" "}
          </Text>
          <Text mb={0}>
            Upon your first visit to our website, a pop-up will appear allowing
            you to set your cookie preferences. By clicking "OK," you consent to
            our use of cookies. It is possible to change your preferences at any
            time through our cookie preferences function.
          </Text>
          {/* Social Media */}
          <Heading as="h2" size="lg">
            What choices do you have on social media?
          </Heading>
          <Text mb={0}>
            Lancewood AI Solutions is active on social media platforms such as
            TikTok, Instagram, YouTube. Through our social media pages, we
            receive visitor statistics. Although Lancewood AI Solutions and the
            social media service providers are jointly responsible for these
            visitor statistics, the respective social media service provider is
            your primary point of contact and handles requests to exercise your
            rights and any complaints. Where necessary, we assist the social
            media provider in responding to your requests or complaints.
          </Text>
          <Text mb={0}>
            For more information about the personal data we receive from social
            media providers and how to adjust settings, please{" "}
          </Text>
          {/* Automated decision-making */}
          <Heading as="h2" size="lg">
            Do we use automated decision-making?
          </Heading>
          <Text mb={0}>
            We do not make decisions that are solely based on automated
            processing, including profiling, that have legal consequences or
            otherwise significantly affect you. This refers to decisions made
            entirely by computer programs or systems, without any human
            intervention (e.g., by one of our employees).
          </Text>

          {/* Outside the EEA */}
          <Heading as="h2" size="lg">
            Do we move personal data to countries outside the EEA?
          </Heading>
          <Text mb={0}>
            In principle, we process all your personal data within the European
            Union and ensure that the parties we work with do the same. However,
            in exceptional cases, it may be necessary to transfer personal data
            to a "third" country outside the European Economic Area (EEA). If
            such a situation arises and your personal data is transferred to a
            country with a lower level of protection than the GDPR, we will take
            appropriate safeguards to securely transfer your personal data to
            that third country, for example, by using a model contract with the
            recipient of the personal data approved by the European Commission.
          </Text>

          {/* Security measures */}
          <Heading as="h2" size="lg">
            What security measures do we apply?
          </Heading>
          <Text mb={0}>
            To protect your personal data, we take appropriate measures in
            accordance with applicable data security laws. This includes
            requiring our service providers to take appropriate measures to
            protect the confidentiality and security of your data.
            Unfortunately, no data transmission or storage system can be
            guaranteed to be 100% secure. Depending on the state of the art, the
            cost of implementation and the nature of the data to be protected,
            we take technical and organizational measures to prevent risks such
            as destruction, loss, modification, unauthorized disclosure of or
            access to your data. If you have reason to believe that your
            interaction with us or your data is no longer being handled
            securely, please contact us immediately.
          </Text>

          {/* Your responsibilities */}
          <Heading as="h2" size="lg">
            What responsibilities do you have?
          </Heading>
          <Text mb={0}>
            We would like to remind you that it is your responsibility to ensure
            that the data you provide to us is accurate, complete, and up to
            date to the best of your knowledge. Additionally, if you share
            others' data with us, you are responsible for collecting that data
            in accordance with local legal requirements. This means that you
            must inform such individuals of the contents of this Statement and
            obtain their prior consent.
          </Text>

          {/* Your rights */}
          <Heading as="h2" size="lg">
            What rights do you have in relation to your personal data?
          </Heading>
          <Text mb={0}>
            You have the right to request access to your personal data
            (including a copy), correct and/or delete it, restrict its
            processing, object to processing, or request the transfer of the
            personal data you have provided. You can do this by contacting us
            using the contact details below. We will do our best to process your
            request in a timely manner and free of charge, unless fulfilling it
            would require disproportionate effort. To prevent abuse, we may ask
            you to adequately identify yourself before processing your request.
            There may be circumstances where we cannot fully comply with your
            request, for example, due to legal retention periods. If such
            circumstances arise, we will inform you accordingly. We typically
            respond within one month of receiving your request.
          </Text>

          {/* Questions or complaints */}
          <Heading as="h2" size="lg">
            Do you have any questions, comments, or complaints?
          </Heading>
          <Text mb={0}>
            If you have any questions, comments, or complaints regarding this
            Statement, we would be happy to assist you. You can contact us via
            email using the contact details provided below. Additionally, you
            have the right to file a complaint with the Dutch Data Protection
            Authority or, if you live or work in a country other than the
            Netherlands, with the relevant privacy regulator in that country.
          </Text>

          {/* Contact */}
          <Heading as="h2" size="lg">
            Contact
          </Heading>
          <Text mb={0}>
            For questions or requests regarding the protection of your personal
            data, or about this Statement in general, please contact us at:
          </Text>
          <Text>Email: info@nomad360.io</Text>
        </VStack>

        {/* Disclaimer linking to Dutch version */}
        <Text mb={8} mt={8} fontStyle="italic" fontWeight="bold">
          This privacy policy is an English translation of the official Dutch
          version. In case of any discrepancies, the Dutch version takes
          precedence. For the Dutch version, click{" "}
          <ChakraLink
            as={RouterLink}
            to="/policies/privacy-policy/nl" // or your actual Dutch Terms route
            color="blue.500"
            textDecoration="underline"
          >
            here
          </ChakraLink>
          .
        </Text>
      </Box>

      {/* Footer & BackToTop */}
      <JoinCommunitySection />
      <Footer />
      <BackToTopButton />
    </Box>
    </>
  );
};

export default PrivacyPolicy;
